import Chip from '@material-ui/core/Chip'
import MenuItem from '@material-ui/core/MenuItem'
import Paper from '@material-ui/core/Paper'
import { withStyles } from '@material-ui/core/styles'
import TextField from '@material-ui/core/TextField'
import Typography from '@material-ui/core/Typography'
import { ArrowDropDown } from '@material-ui/icons'
import CancelIcon from '@material-ui/icons/Cancel'
import cn from 'classnames'
import { chunk, debounce } from 'lodash'
import React from 'react'
import Select from 'react-select'
import AsyncSelect from 'react-select/async'
import styled, { ThemeProvider } from 'styled-components'

import theme from '../../utils/theme'
import LabelWrapper from '../LabelWrapper'

const styles = () => ({
  root: {
    flexGrow: 1,
    position: 'relative',
  },
  input: {
    display: 'flex',
    minWidth: '140px',
    padding: '0 10px',
    color: `var(--gameday-inputField-inputTextColor, ${theme.inputField.inputTextColor})`,
    height: '32px',
  },
  borderWrap: {
    '& > div > div::before': {
      display: 'none',
    },
    '& > div > div::after': {
      display: 'none',
    },
  },
  valueContainer: {
    display: 'flex',
    flexWrap: 'wrap',
    flex: 1,
    alignItems: 'center',
    overflowY: 'auto',
    minHeight: '30px',
    padding: '2px 0',
  },
  wBorder: {
    backgroundColor: `var(--gameday-inputField-inputBGColor, ${theme.inputField.inputBGColor})`,
    border: theme.borders.default,
    borderRadius: '4px',
    padding: '0 0 0 4px',
    minHeight: 'auto',
    height: 'auto',
  },
  chip: {
    backgroundColor: theme.colors.slate,
    color: theme.colors.white,
    height: '24px',
    margin: '1px 2px 1px 0',
    '& .MuiChip-deleteIcon': {
      color: theme.colors.white,
      marginRight: '2px',
    },
  },
  singleValue: {
    fontSize: `${theme.typography.fontSizeSm} !important`,
    [theme.breakpoints.down(400)]: {
      whiteSpace: 'nowrap',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
    },
  },
  placeholder: {
    position: 'absolute',
    left: '10px',
    color: `var(--gameday-inputField-inputTextColor, ${theme.inputField.inputTextColor})`,
    fontSize: theme.typography.fontSizeSm,
  },
  paper: {
    backgroundColor: `var(--gameday-inputField-inputBGColor, ${theme.inputField.inputBGColor})`,
    borderRadius: '4px',
    boxShadow: '2px 2px 10px rgba(98, 99, 99, 0.4)',
    padding: '4px 0',
    position: 'relative',
    zIndex: 2000,
    marginTop: 0,
    minWidth: '240px',
    maxWidth: '420px',
    [theme.breakpoints.down(400)]: {
      maxWidth: '280px',
    },
    '& .MuiListItem-root': {
      fontSize: theme.typography.fontSizeSm,
    },

    '&.isInModal': {
      position: 'fixed',
    },
  },
  dropdownIcon: {
    color: '#747474',
  },
  multiSelect: {
    position: 'initial !important',
    height: 'auto',
  },
})

// function DropdownIndicator(props) {
//   return <ArrowDropDown className={cn('drop-icon', props.selectProps.classes.dropdownIcon)} />
// }
function DropdownIndicator({ selectProps }) {
  const { classes } = selectProps
  return <ArrowDropDown className={cn('drop-icon', classes.dropdownIcon)} />
}

function NoOptionsMessage({ selectProps, innerProps, children }) {
  return (
    <Typography color={theme.colors.slate} className={selectProps.classes.noOptionsMessage} {...innerProps}>
      {children}
    </Typography>
  )
}

function inputComponent({ inputRef, ...props }) {
  return <div ref={inputRef} {...props} />
}

function Control(props) {
  return (
    <SelectLabel
      fullWidth
      InputProps={{
        inputComponent,
        inputProps: {
          className: `${props.selectProps.classes.input}
          ${props.selectProps.bordered && props.selectProps.classes.wBorder}
          multi-select-input`,
          inputRef: props.innerRef,
          children: props.children,
          disabled: props.disabled,
          ...props.innerProps,
        },
      }}
      {...props.selectProps.textFieldProps}
    />
  )
}

function Option(props) {
  return (
    <MenuItem
      buttonRef={props.innerRef}
      selected={props.isFocused}
      component="div"
      style={{
        fontWeight: props.isSelected ? 500 : 400,
      }}
      {...props.innerProps}
    >
      {props.children}
    </MenuItem>
  )
}

function Placeholder(props) {
  return (
    <Typography color={theme.colors.slate} className={props.selectProps.classes.placeholder} {...props.innerProps}>
      {props.children}
    </Typography>
  )
}

function SingleValue(props) {
  return (
    <Typography className={props.selectProps.classes.singleValue} {...props.innerProps}>
      {props.children}
    </Typography>
  )
}

function ValueContainer(props) {
  return <div className={props.selectProps.classes.valueContainer}>{props.children}</div>
}

function MultiValue(props) {
  return (
    <Chip
      tabIndex={-1}
      label={props.children}
      className={cn(props.selectProps.classes.chip, {
        [props.selectProps.classes.chipFocused]: props.isFocused,
        [props.selectProps.classes.chipBordered]: props.selectProps.bordered,
      })}
      onDelete={props.removeProps.onClick}
      deleteIcon={<CancelIcon {...props.removeProps} />}
      aria-label={`Delete ${props.children}`}
    />
  )
}

function Menu(props) {
  return (
    <Paper
      square
      className={cn(props.selectProps.classes.paper, props.selectProps.isInModal && 'isInModal')}
      {...props.innerProps}
    >
      {props.children}
    </Paper>
  )
}

const components = {
  Control,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  Placeholder,
  SingleValue,
  ValueContainer,
  DropdownIndicator,
}

class SelectAutoComplete extends React.Component {
  state = {
    page: 1,
  }

  componentDidMount() {
    const { options } = this.props
    if (options) {
      this.sortOptions()
    }
  }
  componentDidUpdate(prevProps) {
    const { options } = this.props
    if (prevProps.options !== options) {
      this.sortOptions()
    }
  }

  sortOptions = () => {
    const { options } = this.props
    if (!options) return []

    return [...options]
      .filter(option => option.label)
      .sort((a, b) => {
        const labelA = a.label.toLowerCase()
        const labelB = b.label.toLowerCase()
        return labelA.localeCompare(labelB)
      })
  }

  handleChange = name => value => {
    this.setState({
      [name]: value,
    })
  }

  loadOptions = inputValue => {
    const sortedOptions = this.sortOptions()

    // biome-ignore lint/correctness/noUnusedFunctionParameters: TODO
    return new Promise((resolve, reject) => {
      const filtered = sortedOptions.filter(({ label }) => label.toLowerCase().includes(inputValue.toLowerCase()))
      resolve(filtered)
    })
  }

  nextPage = () => {
    const { page } = this.state
    setTimeout(
      () =>
        this.setState({
          page: page + 1,
          isLoading: false,
        }),
      500
    )
  }

  addOptions = () => {
    this.setState({
      isLoading: true,
    })
    debounce(this.nextPage(), 500)
  }

  handleMenuScrollToBottom = () => {
    this.addOptions()
  }

  generateOptions(size, i = 0, newOptions = []) {
    const { options } = this.props
    const sortOptions = options.sort((a, b) => Number(a.order) - Number(b.order))
    if (i === size) return chunk(sortOptions, size)[0]
    const option = chunk(sortOptions, size)
    return this.generateOptions(size, i + 1, [...newOptions, option])
  }

  render() {
    const {
      bordered,
      classes,
      disabled,
      handleChange,
      isClearable,
      isLabelInline,
      isMulti,
      label,
      options,
      placeholder,
      value,
      isMandatory,
      errorMessage,
      customCol,
      customMargin,
      customPadding,
      customWidth,
      fullWidth,
      className,
      errorText,
      helperText,
      id,
      isRequired,
      isToolTip,
      multipleFields,
      noMobile,
      notWrapper,
      noWrap,
      spacing,
      toolTipIcon,
      toolTipIconColor,
      toolTipIconSize,
      toolTipPosition,
      toolTipText,
      withBorder,
      withDivider,
      defaultValue,
      lazyLoad,
      isInModal,
      ...rest
    } = this.props

    const selectStyles = {
      input: () => ({
        color: `var(--gameday-body-textColor, ${theme.body.textColor})`,
        fontSize: theme.typography.fontSizeSm,
        height: '24px',
        '& input': {
          height: '24px',
        },
      }),
      indicatorSeparator: () => ({
        display: 'none',
      }),
      clearIndicator: (base, state) => ({
        ...base,
        padding: state.isFocused && '0 4px',
      }),
      menuList: base => ({
        ...base,
        overscrollBehavior: 'contain',
      }),
    }

    const { page, isLoading } = this.state
    const sortedOptions = this.sortOptions()
    let defaultOptions

    if (lazyLoad) {
      const paginatedOptions = sortedOptions.slice(0, page * 20)
      defaultOptions = [
        ...paginatedOptions,
        ...(isLoading ? [{ value: 'loading', label: 'Loading more...', disabled: true }] : []),
      ]
    }

    return (
      <ThemeProvider theme={theme}>
        <LabelWrapper
          className={className}
          customCol={customCol}
          customMargin={customMargin}
          customPadding={customPadding}
          customWidth={customWidth}
          disabled={disabled}
          errorText={errorText || errorMessage}
          fullWidth={fullWidth}
          helperText={helperText}
          id={id}
          isLabelInline={isLabelInline}
          isRequired={isRequired || isMandatory}
          isToolTip={isToolTip}
          label={label}
          multipleFields={multipleFields}
          noMobile={noMobile}
          notWrapper={notWrapper}
          noWrap={noWrap}
          spacing={spacing}
          toolTipIcon={toolTipIcon}
          toolTipIconColor={toolTipIconColor}
          toolTipIconSize={toolTipIconSize}
          toolTipPosition={toolTipPosition}
          toolTipText={toolTipText}
          withBorder={withBorder || bordered}
          withDivider={withDivider}
          {...rest}
        >
          {lazyLoad ? (
            <AsyncSelect
              classes={classes}
              className={cn('multi-select', classes.multiSelect)}
              disabled={disabled}
              defaultValue={defaultValue}
              isDisabled={disabled}
              styles={selectStyles}
              textFieldProps={{
                InputLabelProps: {
                  shrink: true,
                },
              }}
              style={customWidth ? { width: `${customWidth}px` } : {}}
              components={components}
              value={defaultValue || value}
              onChange={handleChange}
              placeholder="Please select or search..."
              isClearable={isClearable}
              isMulti={isMulti}
              isInModal={isInModal}
              bordered={bordered}
              cacheOptions
              defaultOptions={defaultOptions}
              loadOptions={this.loadOptions}
              onMenuScrollToBottom={this.handleMenuScrollToBottom}
              captureMenuScroll
              id={`${id}-select`}
            />
          ) : (
            <Select
              classes={classes}
              className={cn('multi-select', classes.multiSelect)}
              disabled={disabled}
              defaultValue={defaultValue}
              isDisabled={disabled}
              styles={selectStyles}
              textFieldProps={{
                InputLabelProps: {
                  shrink: true,
                },
              }}
              style={customWidth ? { width: `${customWidth}px` } : {}}
              components={components}
              value={defaultValue || value}
              onChange={handleChange}
              placeholder={placeholder}
              isClearable={isClearable}
              isMulti={isMulti}
              isInModal={isInModal}
              bordered={bordered}
              cacheOptions
              options={sortedOptions}
              id={`${id}-select`}
            />
          )}
        </LabelWrapper>
      </ThemeProvider>
    )
  }
}

SelectAutoComplete.defaultProps = {
  bordered: false,
  isClearable: true,
  notWrapper: true,
  lazyLoad: false,
  isInModal: false,
}

const SelectLabel = styled(TextField)`
  && {
    & > label {
      position: initial;
      font-size: 1rem;
      color: var(--gameday-body-textColorAlt, ${theme.body.textColorAlt});
      transform: scale(1);
      margin-bottom: -4px;
    }
    & > div {
      &:hover {
        cursor: pointer;
      }
      &::before,
      &::after {
        display: none;
      }
      &::after {
        border-color: ${props => props.theme.palette.primary.main};
      }
    }
  }
`

const SelectAC = withStyles(styles)(SelectAutoComplete)
SelectAC.displayName = 'SelectAutoComplete'
export default SelectAC
